import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Shield, 
  Scale, 
  Users, 
  FileText,
  Megaphone,
  Vote,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const ADVOCACY_IMAGE = "https://images.unsplash.com/photo-1550305080-4e029753abcf?w=800&q=80";

const advocacyAreas = [
  {
    icon: Scale,
    title: "Fair Compensation",
    description: "Advocating for transparent and fair commission structures that benefit agents and consumers alike."
  },
  {
    icon: Shield,
    title: "Professional Standards",
    description: "Supporting legislation that maintains high professional standards and protects licensed practitioners."
  },
  {
    icon: Users,
    title: "Consumer Protection",
    description: "Promoting policies that protect buyers and sellers while ensuring efficient real estate transactions."
  },
  {
    icon: FileText,
    title: "Regulatory Reform",
    description: "Working with regulators to streamline compliance requirements without compromising protections."
  }
];

const currentInitiatives = [
  {
    title: "NAR Settlement Implementation",
    status: "Active",
    description: "Ensuring member compliance with new buyer agency requirements while protecting agent interests."
  },
  {
    title: "Affordable Housing Coalition",
    status: "Active",
    description: "Collaborating with policymakers on solutions to increase housing accessibility and affordability."
  },
  {
    title: "Digital Transaction Standards",
    status: "In Progress",
    description: "Developing industry standards for electronic signatures and digital closing processes."
  },
  {
    title: "State Licensing Reciprocity",
    status: "Active",
    description: "Advocating for streamlined licensing across state borders to facilitate agent mobility."
  }
];

const getInvolvedWays = [
  "Join the AREA Advocacy Committee",
  "Participate in legislative briefings",
  "Contact your elected representatives",
  "Share your story with policymakers",
  "Attend advocacy events and rallies",
  "Contribute to the AREA Political Action Fund"
];

export default function Advocacy() {
  return (
    <div className="bg-white" data-testid="advocacy-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-[#D42B3C] font-semibold text-sm tracking-wider uppercase mb-4 block">
                Advocacy
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6">
                Your Voice in the Industry
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                AREA represents the collective voice of real estate professionals across America. 
                We advocate for policies that protect our members, support fair business practices, 
                and advance the real estate profession.
              </p>
              <Button className="bg-[#3D4F7A] hover:bg-[#2A3755] text-white">
                Get Involved
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            <div className="relative">
              <img 
                src={ADVOCACY_IMAGE} 
                alt="Advocacy and community engagement" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Advocacy Focus Areas
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We focus our efforts on issues that matter most to our members and the industry.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {advocacyAreas.map((area) => (
              <Card key={area.title} className="border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300 group">
                <CardContent className="p-8 flex gap-6">
                  <div className="w-14 h-14 rounded-xl bg-[#3D4F7A]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#3D4F7A] transition-colors">
                    <area.icon className="w-7 h-7 text-[#3D4F7A] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">{area.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{area.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Current Initiatives */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Current Initiatives
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Here's what we're actively working on to advance the profession.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {currentInitiatives.map((initiative) => (
              <Card key={initiative.title} className="border-0 shadow-lg bg-white">
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-xl font-semibold text-slate-900">{initiative.title}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      initiative.status === 'Active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {initiative.status}
                    </span>
                  </div>
                  <p className="text-slate-600">{initiative.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How AREA Advocates */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                How AREA Advocates for You
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#3D4F7A] text-white flex items-center justify-center flex-shrink-0">
                    <Megaphone className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Legislative Engagement</h3>
                    <p className="text-slate-600">Direct communication with lawmakers at federal, state, and local levels.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#3D4F7A] text-white flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Coalition Building</h3>
                    <p className="text-slate-600">Partnering with allied organizations to amplify our collective voice.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#3D4F7A] text-white flex items-center justify-center flex-shrink-0">
                    <Vote className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Grassroots Mobilization</h3>
                    <p className="text-slate-600">Empowering members to engage with their elected representatives.</p>
                  </div>
                </div>
              </div>
            </div>
            <Card className="border border-slate-200 shadow-xl">
              <CardContent className="p-8">
                <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-6">Get Involved</h3>
                <div className="space-y-4">
                  {getInvolvedWays.map((way, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#3D4F7A] mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{way}</span>
                    </div>
                  ))}
                </div>
                <Link to="/register" className="block mt-8">
                  <Button className="w-full bg-[#3D4F7A] hover:bg-[#2A3755] text-white">
                    Join the Movement
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#3D4F7A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <Shield className="w-16 h-16 text-white mx-auto mb-6" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Together, We're Stronger
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Join AREA and add your voice to the thousands of professionals 
            working to shape the future of real estate.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-white text-[#3D4F7A] hover:bg-slate-100 px-10 py-6 text-lg font-semibold"
            >
              Join AREA
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
